import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setApiToken, setTokenRefreshCallback } from './api/authApiClient';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminStatus, fetchEmail, fetchTenantId, fetchDistributor } from './app/userInfoSlice';

const TokenContext = createContext(false);
export const useTokenReady = () => useContext(TokenContext);

export const TokenReadyProvider = ({ children }) => {
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    handleRedirectCallback,
    user,
    loginWithRedirect,
    error,
  } = useAuth0();

  const dispatch = useDispatch();
  const userInfoStatus = useSelector(state => state.userInfo.status);
  const [ready, setReady] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [reduxActionsDispatched, setReduxActionsDispatched] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Register token refresh callback
  useEffect(() => {
    const refreshToken = async () => {
      try {
        console.log('🔄 Attempting to refresh token...');
        const token = await getAccessTokenSilently({
          audience: 'https://company-service.api',
          ignoreCache: true
        });
        console.log('✅ Token refreshed successfully');
        return token;
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        
        // If refresh fails, we might need to redirect to login
        if (error.error === 'login_required' || error.error === 'consent_required') {
          console.log('🚪 Token refresh failed, redirecting to login...');
          loginWithRedirect();
        }
        throw error;
      }
    };
    
    setTokenRefreshCallback(refreshToken);
  }, [getAccessTokenSilently, loginWithRedirect]);

  // Give Auth0 time to check for existing session before redirecting
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🕒 Initial load timeout - Auth0 should have completed session check');
      setInitialLoadComplete(true);
    }, 300); // Give Auth0 300ms to detect session - reduced from 500ms

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    console.log('🔍 Auth0 State:', { isLoading, isAuthenticated, hasUser: !!user, error: error?.message });
    
    const setup = async () => {
      // Clear URL parameters if we have a callback URL but are already authenticated
      const isCallback = window.location.search.includes("code=") && window.location.search.includes("state=");
      if (isCallback && isAuthenticated) {
        console.log('🧹 Clearing callback URL since user is already authenticated');
        window.history.replaceState({}, document.title, "/");
      }

      // Step 1: Handle redirect callback only if not authenticated
      if (isCallback && !isAuthenticated) {
        console.log('📥 Handling Auth0 callback...');
        try {
          await handleRedirectCallback();
          window.history.replaceState({}, document.title, "/");
          console.log('✅ Callback handled successfully');
        } catch (err) {
          console.error("❌ Error during handleRedirectCallback:", err);
          // Clear URL and continue - let Auth0 handle it
          window.history.replaceState({}, document.title, "/");
        }
        return; // Exit and let the next cycle handle the authenticated state
      }

      // Step 2: If not authenticated after enough time, redirect to login (ignore invalid state errors)
      if (!isLoading && !isAuthenticated && !hasRedirected && initialLoadComplete) {
        // Don't redirect if we have an invalid state error - it means Auth0 is confused
        if (error?.message?.includes("Invalid state")) {
          console.log('⚠️ Invalid state error detected, not redirecting to avoid loops');
          return;
        }
        
        console.log('🚪 No session found after initial load, redirecting to login...');
        setHasRedirected(true);
        loginWithRedirect();
        return;
      }

      // Step 3: If authenticated, proceed with token setup (ignore any errors)
      if (!isLoading && isAuthenticated && !reduxActionsDispatched) {
        console.log('✅ User authenticated, setting up tokens and Redux...');
        
        if (error?.message?.includes("Invalid state")) {
          console.log('🔄 Proceeding despite invalid state error...');
        }
        
        try {
          const token = await getAccessTokenSilently({
            audience: 'https://company-service.api',
            ignoreCache: true // Always get fresh token to avoid state issues
          });
          setApiToken(token);
          if (user?.sub) localStorage.setItem('subId', user.sub);
          
          // Now that token is set, populate Redux store.
          // IMPORTANT: await ALL three thunks before marking ready. The shared
          // `status` flag flips to 'idle' as soon as the FIRST thunk fulfills,
          // so it cannot tell us admin status is loaded. Without awaiting, an
          // admin can render before fetchAdminStatus resolves (isAdmin still the
          // initial `false`) and get dropped onto the tenant dashboard.
          console.log('🚀 Token set, dispatching Redux actions...');
          await Promise.all([
            dispatch(fetchAdminStatus()).unwrap().catch((e) => console.warn('fetchAdminStatus failed', e)),
            dispatch(fetchTenantId()).unwrap().catch((e) => console.warn('fetchTenantId failed', e)),
            dispatch(fetchEmail()).unwrap().catch((e) => console.warn('fetchEmail failed', e)),
            dispatch(fetchDistributor()).unwrap().catch((e) => console.warn('fetchDistributor failed', e)),
          ]);
          console.log('✅ Admin/tenant/email loaded, marking Redux actions dispatched');
          setReduxActionsDispatched(true);
          
        } catch (err) {
          console.error("❌ Failed to get access token", err);
          
          // Handle different error scenarios
          if (err.error === 'login_required' || err.error === 'consent_required') {
            console.log('🔑 Re-authentication required, redirecting...');
            setHasRedirected(true);
            loginWithRedirect();
          } else if (err.error === 'timeout' || err.message?.includes('network')) {
            console.log('🌐 Network/timeout error, retrying in a moment...');
            // Retry after a short delay for network issues
            setTimeout(() => {
              if (isAuthenticated && !reduxActionsDispatched) {
                console.log('🔄 Retrying token fetch after network error...');
                setup();
              }
            }, 2000);
          } else if (err.message?.includes('Invalid state')) {
            console.log('⚠️ Invalid state error during token fetch, forcing fresh login...');
            // Clear Auth0 cache and force fresh login for state issues
            try {
              await getAccessTokenSilently({ 
                audience: 'https://company-service.api',
                ignoreCache: true,
                cacheMode: 'off'
              });
            } catch (retryErr) {
              console.log('🔄 Fresh token fetch failed, redirecting to login...');
              setHasRedirected(true);
              loginWithRedirect();
            }
          } else {
            console.log('🔄 Unknown error, attempting fresh login...');
            setHasRedirected(true);
            loginWithRedirect();
          }
        }
      }
    };

    setup();
  }, [isLoading, isAuthenticated, getAccessTokenSilently, user, loginWithRedirect, handleRedirectCallback, hasRedirected, dispatch, reduxActionsDispatched, initialLoadComplete, error]);

  // Wait for Redux actions to complete before setting ready
  useEffect(() => {
    if (reduxActionsDispatched && userInfoStatus === 'idle') {
      console.log('✅ Redux actions completed, setting ready to true');
      setReady(true);
    }
  }, [reduxActionsDispatched, userInfoStatus]);

  // Periodic token refresh to prevent expiration (every 45 minutes)
  useEffect(() => {
    if (!isAuthenticated || !ready) return;

    const refreshInterval = setInterval(async () => {
      try {
        console.log('🕒 Periodic token refresh...');
        const token = await getAccessTokenSilently({
          audience: 'https://company-service.api',
          ignoreCache: true
        });
        setApiToken(token);
        console.log('✅ Periodic token refresh successful');
      } catch (error) {
        console.warn('⚠️ Periodic token refresh failed:', error);
        // Don't redirect here - let the 401 interceptor handle it when an actual request fails
      }
    }, 45 * 60 * 1000); // 45 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, ready, getAccessTokenSilently]);

  if (!ready) {
    let loadingMessage = "Loading...";
    
    if (isLoading) {
      loadingMessage = "Loading...";
    } else if (!isAuthenticated && !initialLoadComplete) {
      loadingMessage = "Loading...";
    } else if (isAuthenticated && reduxActionsDispatched && userInfoStatus === 'loading') {
      loadingMessage = "Loading...";
    }

    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">{loadingMessage}</Typography>
        {/* Remove error display from user view - errors are logged to console */}
      </Box>
    );
  }

  return (
    <TokenContext.Provider value={true}>
      {children}
    </TokenContext.Provider>
  );
};
